import React from 'react'
import styled, { css } from 'styled-components'
import Modal from 'components/Modal'
import { ModalInjectedProps } from 'containers/ModalManager'
import { Flex } from 'components/Box'
import { ReactComponent as CloseSvg } from 'icons/close-overlay-24.svg'
import isMac from 'utils/isMac'

/**
 * CONSTANTS
 */
const KEY_MODIFIER = isMac() ? '⌘' : 'Ctrl'

/**
 * STYLES
 */
const StyledModal = styled(Modal)`
  position: relative;
  width: 390px;
`

const Header = styled(Modal.Header)`
  ${() => css`
    margin: 36px 36px 0;
  `};
`

const CloseIcon = styled(CloseSvg)`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 24px;
  height: 24px;
  cursor: pointer;
`

const Body = styled(Modal.Body)`
  margin: 0 36px 24px;
`

const Title = styled.h4`
  font-size: 1.25rem;
`

const List = styled(Flex)`
  flex-direction: column;
  color: ${({ theme }) => theme.colors.text.dark.base};
`

const Item = styled(Flex)`
  justify-content: space-between;
  font-size: 0.875rem;
  color: ${({ theme, secondary }) =>
    secondary
      ? theme.colors.text.dark.tertiary
      : theme.colors.text.dark.primary};

  & + & {
    margin-top: 18px; /* stylelint-disable-line scales/space */
  }
`

const KeyboardShortcut = styled(Flex)`
  min-width: 24px;
  justify-content: center;
  align-items: center;
  height: 24px;
  padding: 0 6px; /* stylelint-disable-line scales/space */
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: inset 0 -1px rgba(0, 0, 0, 0.05);
  border-radius: 3px;

  & + & {
    margin-left: 6px; /* stylelint-disable-line scales/space */
  }
`

const KeyboardShortcutOr = styled.div.attrs({ children: 'or' })`
  margin: 0 6px; /* stylelint-disable-line scales/space */
`

const Separator = styled.hr`
  border-color: rgba(0, 0, 0, 0.06);
  margin: 24px -36px;
`

/**
 * COMPONENTS
 */
export const KeyboardShortcutsModal: React.FC<ModalInjectedProps> = ({
  hideModal,
}) => (
  <StyledModal onCancel={hideModal}>
    <CloseIcon
      data-testid="close-keyboard-shortcuts-modal"
      onClick={hideModal}
    />
    <Header>Keyboard Shortcuts</Header>
    <Body>
      <List>
        <Title>Documents View</Title>
        <Item>
          Search
          <KeyboardShortcut>/</KeyboardShortcut>
        </Item>
        <Item>
          Grid view
          <KeyboardShortcut>[</KeyboardShortcut>
        </Item>
        <Item>
          List view
          <KeyboardShortcut>]</KeyboardShortcut>
        </Item>
        <Title>Artboard View</Title>
        <Item>
          Zoom at 100%
          <KeyboardShortcut>0</KeyboardShortcut>
        </Item>
        <Item>
          Fit to Screen
          <KeyboardShortcut>1</KeyboardShortcut>
        </Item>
        <Item>
          Zoom In
          <KeyboardShortcut>+</KeyboardShortcut>
        </Item>
        <Item>
          Zoom Out
          <KeyboardShortcut>-</KeyboardShortcut>
        </Item>
        <Item>
          Navigate Artboards
          <Flex>
            <KeyboardShortcut>←</KeyboardShortcut>
            <KeyboardShortcut>→</KeyboardShortcut>
          </Flex>
        </Item>
        <Title>Inspector</Title>
        <Item>
          Deselect
          <KeyboardShortcut>ESC</KeyboardShortcut>
        </Item>
        <Item>
          Show Layer Menu
          <KeyboardShortcut>Right-click</KeyboardShortcut>
        </Item>
        <Title>Prototype</Title>
        <Item>
          Show Hotspots
          <Flex>
            <KeyboardShortcut>Shift</KeyboardShortcut>
            <KeyboardShortcutOr />
            <KeyboardShortcut>Click</KeyboardShortcut>
          </Flex>
        </Item>
        <Item>
          Close Prototype
          <KeyboardShortcut>ESC</KeyboardShortcut>
        </Item>
        <Item>
          Close Prototype (Touch Devices)
          <KeyboardShortcut>Long press</KeyboardShortcut>
        </Item>
        <Separator />
        <Item secondary>
          Open these shortcuts by pressing
          <Flex>
            <KeyboardShortcut>{KEY_MODIFIER}</KeyboardShortcut>
            <KeyboardShortcut>/</KeyboardShortcut>
          </Flex>
        </Item>
      </List>
    </Body>
  </StyledModal>
)
